import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  X,
  Lock,
  ArrowRight,
  Apple,
  Smartphone,
  Settings,
  ChevronDown,
  Loader2,
  AlertTriangle,
  Check,
  Copy,
  Mail,
  RefreshCw,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { QRCodeCanvas } from "qrcode.react";
import { useAuth } from "@/context/AuthContext";
import { api, API_BASE } from "@/lib/api";

/**
 * IntegrationModal — Phase 6 fix (UX rewrite).
 *
 * The previous version surfaced raw curl, headers, JSON bodies and 7-step
 * iOS recipes. Producto rejected that as "breaks the elite experience".
 * This rewrite shows ZERO code: 3 sections (Siri / Android / Advanced),
 * each driven by a single primary CTA.
 *
 * - Siri:    "Generate Shortcut Link" → backend builds a binary .shortcut
 *            plist with the user's token baked in. The frontend renders the
 *            URL as a QR code (scan from iPhone) + "Copy link" + "Email".
 * - Android: "Copy Android link" → puts a deep-link to /api/ingest/quick
 *            (token embedded) on the clipboard.
 * - Advanced (collapsible, closed by default): rotate token (with confirm).
 *
 * The integration_token is NEVER displayed. It only exists inside the URLs
 * we generate, and even then it stays out of any visible code block.
 *
 * Phase 8 hook: when the user is not on Elite Mastermind, this whole modal
 * is replaced by a compact upgrade prompt (kept from the Fase 8 gate).
 */

function CopyButton({ text, label, copiedLabel, testid, disabled }) {
  const [copied, setCopied] = useState(false);
  const onClick = useCallback(async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked — silently no-op */
    }
  }, [text]);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || !text}
      data-testid={testid}
      className="
        inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full
        bg-white/[0.04] border border-white/[0.1]
        text-[11.5px] text-[#E5E7EB]
        hover:bg-white/[0.08] hover:text-[#00E5FF] hover:border-[rgba(0,229,255,0.4)]
        transition-colors disabled:opacity-40 disabled:cursor-not-allowed
      "
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5 text-[#00E5FF]" strokeWidth={2.5} />
          <span className="text-[#00E5FF]">{copiedLabel || "Copiado"}</span>
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5" strokeWidth={1.75} />
          {label}
        </>
      )}
    </button>
  );
}

function PrimaryCTA({ onClick, disabled, done, doneLabel, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || done}
      data-testid={testid}
      className={`
        w-full h-11 rounded-full text-[12.5px] font-semibold
        inline-flex items-center justify-center gap-1.5
        transition-colors
        ${done
          ? "bg-[rgba(0,229,255,0.08)] border border-[rgba(0,229,255,0.5)] text-[#00E5FF] cursor-default"
          : "bg-[rgba(0,229,255,0.16)] border border-[rgba(0,229,255,0.55)] text-[#00E5FF] hover:bg-[rgba(0,229,255,0.24)] shadow-[0_0_22px_rgba(0,229,255,0.3)]"}
        disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none
      `}
    >
      {done ? (
        <>
          <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
          {doneLabel || "Listo"}
        </>
      ) : (
        children
      )}
    </button>
  );
}

function Section({ icon: Icon, title, subtitle, children, testid }) {
  return (
    <section
      data-testid={testid}
      className="
        rounded-2xl p-4 sm:p-5
        bg-white/[0.025] border border-[rgba(0,229,255,0.12)]
      "
    >
      <div className="flex items-start gap-3 mb-3">
        <div
          className="h-9 w-9 rounded-full shrink-0 bg-[rgba(0,229,255,0.08)] border border-[rgba(0,229,255,0.35)] flex items-center justify-center"
          aria-hidden="true"
        >
          <Icon className="h-4 w-4 text-[#00E5FF]" strokeWidth={1.75} />
        </div>
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-[#E5E7EB] leading-tight">
            {title}
          </p>
          {subtitle ? (
            <p className="mt-0.5 text-[11.5px] text-[#9CA3AF] leading-snug">
              {subtitle}
            </p>
          ) : null}
        </div>
      </div>
      {children}
    </section>
  );
}

export default function IntegrationModal({ open, onClose }) {
  const { user, refreshMe } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const downOnBackdropRef = useRef(false);

  const [siriLink, setSiriLink] = useState(null);
  const [siriCopiedToast, setSiriCopiedToast] = useState(null);
  const [androidLink, setAndroidLink] = useState(null);
  const [androidCopied, setAndroidCopied] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const isElite = (user?.subscription?.tier || "free") === "elite";
  const apiBase = useMemo(
    () => (API_BASE || `${process.env.REACT_APP_BACKEND_URL || ""}/api`).replace(/\/$/, ""),
    [],
  );

  // Reset on open / close.
  useEffect(() => {
    if (!open) {
      setSiriLink(null);
      setSiriCopiedToast(null);
      setAndroidLink(null);
      setAndroidCopied(false);
      setAdvancedOpen(false);
      setConfirmRotate(false);
      setRotating(false);
      setErrorMsg(null);
    }
  }, [open]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Escape closes.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handleGenerateSiri = useCallback(() => {
    const token = user?.integration_token;
    if (!token) {
      setErrorMsg("No se pudo generar el enlace. Reintenta.");
      return;
    }
    // The .shortcut file lives at this URL with the token baked in.
    setSiriLink(`${apiBase}/integrations/siri/shortcut?t=${token}`);
  }, [user?.integration_token, apiBase]);

  const handleCopyAndroid = useCallback(async () => {
    const token = user?.integration_token;
    if (!token) return;
    const link = `${apiBase}/ingest/quick?t=${token}&text=&format=html`;
    try {
      await navigator.clipboard.writeText(link);
      setAndroidLink(link);
      setAndroidCopied(true);
      setTimeout(() => setAndroidCopied(false), 1800);
    } catch {
      /* ignore */
    }
  }, [user?.integration_token, apiBase]);

  const handleRotate = useCallback(async () => {
    setRotating(true);
    setErrorMsg(null);
    try {
      await api.post("/me/integration-token/rotate");
      if (refreshMe) await refreshMe();
      // Invalidate any previously generated link.
      setSiriLink(null);
      setAndroidLink(null);
      setAndroidCopied(false);
      setConfirmRotate(false);
      setAdvancedOpen(false);
    } catch (e) {
      const status = e?.response?.status;
      setErrorMsg(
        status === 429
          ? "Demasiadas rotaciones. Reintenta en un minuto."
          : "No se pudo regenerar el enlace. Reintenta.",
      );
    } finally {
      setRotating(false);
    }
  }, [refreshMe]);

  if (!open) return null;

  // Phase 8 gate: non-Elite users see an upgrade prompt instead.
  if (!isElite) {
    return (
      <div
        data-testid="integration-overlay"
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/65 backdrop-blur-sm animate-fade-in-up px-4"
        role="dialog"
        onMouseDown={(e) => {
          downOnBackdropRef.current = e.target === e.currentTarget;
        }}
        onMouseUp={(e) => {
          const ok = downOnBackdropRef.current && e.target === e.currentTarget;
          downOnBackdropRef.current = false;
          if (ok) onClose();
        }}
      >
        <div
          data-testid="integration-locked-modal"
          className="relative w-full max-w-[360px] rounded-[24px] px-6 pt-7 pb-5 bg-[rgba(10,14,26,0.96)] backdrop-blur-xl border border-[rgba(0,229,255,0.4)] shadow-[0_30px_80px_-10px_rgba(0,0,0,0.75),0_0_60px_-12px_rgba(0,229,255,0.45)]"
        >
          <button
            type="button"
            onClick={onClose}
            data-testid="integration-locked-close"
            aria-label={t("common.close")}
            className="absolute top-3 right-3 h-8 w-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-white/[0.06] hover:text-[#E5E7EB]"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
          <div
            className="h-12 w-12 rounded-full mx-auto mb-3 bg-[rgba(0,229,255,0.1)] border border-[rgba(0,229,255,0.5)] flex items-center justify-center shadow-[0_0_24px_rgba(0,229,255,0.45)]"
            aria-hidden="true"
          >
            <Lock className="h-5 w-5 text-[#00E5FF]" strokeWidth={1.75} />
          </div>
          <p className="text-[10.5px] uppercase tracking-[0.24em] text-[#00E5FF]/85 text-center">
            Elite Mastermind
          </p>
          <h3
            className="mt-1 text-[18px] font-semibold text-[#E5E7EB] text-center leading-tight"
            data-testid="integration-locked-title"
          >
            {t("integration.locked_title")}
          </h3>
          <p className="mt-2 text-[12.5px] text-[#9CA3AF] text-center leading-relaxed">
            {t("integration.locked_message")}
          </p>
          <button
            type="button"
            onClick={() => {
              onClose();
              navigate("/billing");
            }}
            data-testid="integration-locked-cta"
            className="mt-5 w-full h-11 rounded-full text-[13px] font-semibold bg-[rgba(0,229,255,0.18)] border border-[rgba(0,229,255,0.55)] text-[#00E5FF] hover:bg-[rgba(0,229,255,0.28)] shadow-[0_0_22px_rgba(0,229,255,0.35)] inline-flex items-center justify-center gap-1.5"
          >
            {t("integration.see_plans")}
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="integration-overlay"
      className="
        fixed inset-0 z-[10000]
        flex items-start sm:items-center justify-center
        bg-black/65 backdrop-blur-sm
        animate-fade-in-up px-3 py-6 sm:p-6
        overflow-y-auto
      "
      role="dialog"
      aria-label={t("integration.title")}
      onMouseDown={(e) => {
        downOnBackdropRef.current = e.target === e.currentTarget;
      }}
      onMouseUp={(e) => {
        const ok = downOnBackdropRef.current && e.target === e.currentTarget;
        downOnBackdropRef.current = false;
        if (ok) onClose();
      }}
    >
      <div
        ref={containerRef}
        data-testid="integration-modal"
        className="
          relative w-full max-w-[440px]
          rounded-[24px] px-5 sm:px-6 pt-7 pb-6
          bg-[rgba(10,14,26,0.96)] backdrop-blur-xl
          border border-[rgba(0,229,255,0.3)]
          shadow-[0_30px_80px_-10px_rgba(0,0,0,0.75),0_0_60px_-12px_rgba(0,229,255,0.4)]
        "
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t("common.close")}
          data-testid="integration-close"
          className="absolute top-3 right-3 h-9 w-9 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-white/[0.06] hover:text-[#E5E7EB] transition-colors"
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>

        <p className="text-[10.5px] uppercase tracking-[0.24em] text-[#00E5FF]/85">
          {t("integration.eyebrow")}
        </p>
        <h2
          className="mt-1 text-[20px] font-semibold text-[#E5E7EB] leading-tight"
          data-testid="integration-title"
        >
          Integración con Asistentes
        </h2>
        <p className="mt-2 text-[12.5px] text-[#9CA3AF] leading-relaxed">
          Conecta NexAgenda con Siri (iOS) o Google Assistant (Android) para
          comandar con voz sin abrir la app.
        </p>

        <div className="mt-5 space-y-3">
          {/* SECTION 1 — Siri */}
          <Section
            icon={Apple}
            title="Integración con Siri (iOS)"
            subtitle="Crea tu atajo en un toque."
            testid="integration-section-siri"
          >
            {!siriLink ? (
              <PrimaryCTA
                onClick={handleGenerateSiri}
                testid="integration-siri-generate"
              >
                Generar Enlace de Atajo
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
              </PrimaryCTA>
            ) : (
              <div className="space-y-3" data-testid="integration-siri-result">
                <div className="flex flex-col items-center pt-1">
                  <div
                    className="rounded-2xl p-3 bg-[#03060C] border border-[rgba(0,229,255,0.25)] shadow-[0_0_24px_-8px_rgba(0,229,255,0.35)]"
                    data-testid="integration-siri-qr"
                  >
                    <QRCodeCanvas
                      value={siriLink}
                      size={180}
                      bgColor="#03060C"
                      fgColor="#00E5FF"
                      level="M"
                      marginSize={1}
                    />
                  </div>
                  <p className="mt-3 text-[11.5px] text-[#9CA3AF] text-center max-w-[280px] leading-relaxed">
                    Escanea desde tu iPhone para abrir en Safari y añadir
                    "NexAgenda" a tus Atajos.
                  </p>
                </div>

                <div className="flex items-center justify-center gap-2 flex-wrap">
                  <CopyButton
                    text={siriLink}
                    label="Copiar enlace"
                    copiedLabel="Enlace copiado"
                    testid="integration-siri-copy"
                  />
                  <a
                    href={`mailto:?subject=${encodeURIComponent("Mi atajo de NexAgenda")}&body=${encodeURIComponent(`Abre este enlace desde tu iPhone para añadir NexAgenda a Atajos:\n\n${siriLink}`)}`}
                    data-testid="integration-siri-email"
                    className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-white/[0.04] border border-white/[0.1] text-[11.5px] text-[#E5E7EB] hover:bg-white/[0.08] hover:text-[#00E5FF] hover:border-[rgba(0,229,255,0.4)] transition-colors"
                  >
                    <Mail className="h-3.5 w-3.5" strokeWidth={1.75} />
                    Enviar por email
                  </a>
                </div>

                <p className="text-[11.5px] text-[#9CA3AF] leading-relaxed text-center pt-1">
                  Al abrir desde iPhone verás Atajos preguntándote si deseas
                  añadir <span className="text-[#E5E7EB]">"NexAgenda"</span>.
                  Toca <span className="text-[#00E5FF]">Añadir</span> y listo —
                  ya puedes decir <span className="text-[#00E5FF]">"Oye Siri, NexAgenda"</span>.
                </p>

                <p className="text-[10.5px] text-[#00E5FF]/80 text-center inline-flex items-center justify-center gap-1 w-full">
                  <Check className="h-3 w-3" strokeWidth={2.5} />
                  Enlace generado
                </p>
              </div>
            )}
          </Section>

          {/* SECTION 2 — Android */}
          <Section
            icon={Smartphone}
            title="Integración con Android"
            subtitle="Usa el enlace rápido con tu asistente favorito."
            testid="integration-section-android"
          >
            <PrimaryCTA
              onClick={handleCopyAndroid}
              done={androidCopied}
              doneLabel="Enlace copiado"
              testid="integration-android-copy"
            >
              <Copy className="h-3.5 w-3.5" strokeWidth={2} />
              Copiar enlace para Android
            </PrimaryCTA>
            {androidLink ? (
              <p
                className="mt-3 text-[11.5px] text-[#9CA3AF] leading-relaxed"
                data-testid="integration-android-hint"
              >
                Pégalo en Google Assistant (vía Tasker / AutoVoice) o en tu
                widget de atajos.
              </p>
            ) : null}
          </Section>

          {/* SECTION 3 — Advanced */}
          <div
            className="rounded-2xl bg-white/[0.02] border border-white/[0.06]"
            data-testid="integration-section-advanced"
          >
            <button
              type="button"
              onClick={() => setAdvancedOpen((v) => !v)}
              data-testid="integration-advanced-toggle"
              data-open={advancedOpen ? "true" : "false"}
              aria-expanded={advancedOpen}
              className="w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 text-left"
            >
              <span className="inline-flex items-center gap-2.5">
                <Settings className="h-3.5 w-3.5 text-[#9CA3AF]" strokeWidth={1.75} />
                <span className="text-[12.5px] text-[#E5E7EB]">
                  Opciones avanzadas
                </span>
              </span>
              <ChevronDown
                className={`h-4 w-4 text-[#9CA3AF] transition-transform duration-200 ${advancedOpen ? "rotate-180" : ""}`}
                strokeWidth={2}
              />
            </button>
            {advancedOpen ? (
              <div
                className="px-4 sm:px-5 pb-4 pt-0 animate-fade-in-up"
                data-testid="integration-advanced-content"
              >
                <p className="text-[11.5px] text-[#9CA3AF] leading-relaxed">
                  Si compartiste tu enlace con alguien o sospechas que se ha
                  filtrado, regenera la conexión.
                </p>
                {!confirmRotate ? (
                  <button
                    type="button"
                    onClick={() => setConfirmRotate(true)}
                    data-testid="integration-revoke-button"
                    className="
                      mt-3 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full
                      text-[11.5px] text-[#9CA3AF]
                      bg-white/[0.02] border border-white/[0.06]
                      hover:text-[#E5E7EB] hover:bg-white/[0.05]
                      transition-colors
                    "
                  >
                    <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.75} />
                    Revocar enlace y generar uno nuevo
                  </button>
                ) : (
                  <div
                    className="mt-3 p-3 rounded-xl bg-[rgba(245,158,11,0.06)] border border-[rgba(245,158,11,0.3)]"
                    data-testid="integration-revoke-confirm"
                  >
                    <p className="text-[11.5px] text-[#FBBF24] flex items-start gap-1.5 leading-relaxed">
                      <AlertTriangle
                        className="h-3.5 w-3.5 shrink-0 mt-0.5"
                        strokeWidth={1.75}
                      />
                      <span>
                        ¿Seguro? Todos los atajos previos dejarán de funcionar.
                        Tendrás que generar un enlace nuevo para Siri y copiar
                        el nuevo enlace para Android.
                      </span>
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setConfirmRotate(false)}
                        data-testid="integration-revoke-cancel"
                        disabled={rotating}
                        className="flex-1 h-8 rounded-full text-[11.5px] border border-white/[0.1] text-[#E5E7EB] hover:bg-white/[0.04] disabled:opacity-50"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={handleRotate}
                        disabled={rotating}
                        data-testid="integration-revoke-confirm-btn"
                        className="flex-1 h-8 rounded-full text-[11.5px] font-semibold bg-[rgba(0,229,255,0.18)] border border-[rgba(0,229,255,0.5)] text-[#00E5FF] hover:bg-[rgba(0,229,255,0.28)] disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                      >
                        {rotating ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} /> : null}
                        Sí, regenerar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>

        {errorMsg ? (
          <p
            data-testid="integration-error"
            className="mt-4 px-3 py-2 rounded-xl text-[11.5px] text-[#F87171] bg-[rgba(239,68,68,0.06)] border border-[rgba(239,68,68,0.3)]"
          >
            {errorMsg}
          </p>
        ) : null}
      </div>

      {siriCopiedToast ? (
        <div
          data-testid="integration-toast"
          className="fixed bottom-10 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-[rgba(0,229,255,0.18)] border border-[rgba(0,229,255,0.55)] text-[#00E5FF] text-[12px]"
        >
          {siriCopiedToast}
        </div>
      ) : null}
    </div>
  );
}
